import { useEffect, useState } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import { Mountain, ArrowLeft, Check, Wifi, Snowflake, Car, UtensilsCrossed, Sparkles, Users, MapPin } from "lucide-react";
import api, { resolveImg } from "../lib/api";
import { SITE_URL, BUSINESS, aptUrl } from "../lib/site";
import BookingModal from "../components/BookingModal";
import ConciergeWidget from "../components/ConciergeWidget";
import Panorama360 from "../components/Panorama360";
import AvailabilityCalendar from "../components/AvailabilityCalendar";
import Seo from "../components/Seo";
import LanguageSwitcher from "../components/LanguageSwitcher";
import { useI18n } from "../context/I18nContext";

function todayPlus(d) { const x = new Date(); x.setDate(x.getDate() + d); return x.toISOString().slice(0, 10); }
function isoPlus(isoDate, d) { const x = new Date(isoDate + "T00:00:00"); x.setDate(x.getDate() + d); return x.toISOString().slice(0, 10); }
const ICONS = { "Wi-Fi veloce": Wifi, "Deposito sci riscaldato": Snowflake, "Parcheggio privato": Car, "Cucina attrezzata": UtensilsCrossed };

export default function ApartmentDetail() {
  const { id, localita, slug } = useParams();
  const { t } = useI18n();
  const [searchParams] = useSearchParams();
  const [apt, setApt] = useState(null);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [pickedCheckin, setPickedCheckin] = useState("");
  const [calRoomId, setCalRoomId] = useState(null);

  useEffect(() => {
    const url = slug ? `/apartments/slug/${localita}/${slug}` : `/apartments/${id}`;
    api.get(url).then((r) => setApt(r.data)).catch(() => {});
  }, [id, localita, slug]);

  // Deep-link from quote email: /appartamento/:id?book=1&checkin=..&checkout=..&adults=..&children=..&pets=1&room=RID
  useEffect(() => {
    if (!apt || searchParams.get("book") !== "1") return;
    const roomId = searchParams.get("room");
    const room = roomId ? (apt.room_types || []).find((r) => r.id === roomId) || null : null;
    setSelectedRoom(room);
    setOpen(true);
  }, [apt, searchParams]);

  const bookingInitial = {
    checkin: pickedCheckin || searchParams.get("checkin") || todayPlus(14),
    checkout: pickedCheckin ? isoPlus(pickedCheckin, 3) : (searchParams.get("checkout") || todayPlus(17)),
    adults: Number(searchParams.get("adults")) || 2,
    children: Number(searchParams.get("children")) || 0,
    pets: searchParams.get("pets") === "1",
    discount_type: searchParams.get("discount_type") || "percent",
    discount_value: Number(searchParams.get("discount_value")) || 0,
    phone: searchParams.get("phone") || "",
    name: searchParams.get("name") || "",
  };

  if (!apt) return <div className="min-h-screen flex items-center justify-center bg-ivory text-fir">Caricamento…</div>;

  const isHotel = apt.type === "hotel";
  const gallery = apt.images?.length ? apt.images : [];
  const activeCalRoom = isHotel ? (calRoomId || apt.room_types?.[0]?.id || null) : null;

  const sizeLabel = [
    apt.bedrooms ? `${apt.bedrooms} camer${apt.bedrooms > 1 ? "e" : "a"} da letto` : null,
    apt.bathrooms ? `${apt.bathrooms} ${apt.bathrooms > 1 ? "bagni" : "bagno"}` : null,
    apt.max_guests ? `fino a ${apt.max_guests} ospiti` : null,
  ].filter(Boolean).join(" · ");
  const topAmenities = (apt.amenities || []).slice(0, 4).join(", ");
  const seoTitle = `${apt.title} · ${apt.max_guests || 2} ospiti a Dimaro, Val di Sole`;
  const seoDesc = `${apt.title} a Dimaro Folgarida (Val di Sole): ${sizeLabel || "appartamento indipendente"}. ${apt.subtitle ? apt.subtitle + ". " : ""}${(apt.description || "").slice(0, 110)}${apt.description ? "… " : ""}Prenotazione diretta al miglior prezzo, vicino agli impianti di Folgarida-Marilleva.`;

  const bookRoom = (room) => { setPickedCheckin(""); setSelectedRoom(room || null); setOpen(true); };
  const pickDate = (isoDate, room) => { setPickedCheckin(isoDate); setSelectedRoom(room || null); setOpen(true); };

  const mapQuery = apt.latitude && apt.longitude
    ? `${apt.latitude},${apt.longitude}`
    : (apt.address || `${apt.title}, Dimaro, Val di Sole, Trentino`);
  const aptFaq = [
    { q: `Dove si trova ${apt.title}?`, a: `${apt.title} si trova a Dimaro Folgarida, in Val di Sole (Trentino), vicino agli impianti di Folgarida-Marilleva e alle Dolomiti di Brenta.` },
    { q: `Quante persone ospita ${apt.title}?`, a: `${apt.title} ospita ${apt.max_guests ? `fino a ${apt.max_guests} persone` : "coppie e famiglie"}${apt.bedrooms ? `, con ${apt.bedrooms} camere da letto` : ""}${apt.bathrooms ? ` e ${apt.bathrooms} ${apt.bathrooms > 1 ? "bagni" : "bagno"}` : ""}.` },
    { q: "È vicino agli impianti da sci?", a: "Sì, gli impianti di Folgarida-Marilleva sono raggiungibili in pochi minuti d'auto o con lo skibus della Val di Sole." },
    { q: "Come si prenota?", a: "Verifica le date sul calendario di disponibilità e prenota online: pagando la caparra confermi subito il soggiorno." },
    { q: "Conviene prenotare dal sito ufficiale?", a: "Sì, prenotando direttamente hai il miglior prezzo senza commissioni e il contatto diretto con il proprietario." },
  ];
  const checkinTime = "15:00";
  const checkoutTime = "10:00";
  const offerPrice = apt.from_price ?? apt.base_price;
  const ldImages = (apt.images || []).map((im) => resolveImg(im)).filter(Boolean).slice(0, 12);
  const canonicalUrl = `${SITE_URL}${aptUrl(apt)}`;
  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": isHotel ? "Hotel" : "VacationRental",
        "additionalType": "https://schema.org/Accommodation",
        "identifier": apt.id,
        "name": apt.title,
        "description": apt.description || apt.subtitle,
        "url": canonicalUrl,
        "image": ldImages,
        "priceRange": "€€",
        "telephone": BUSINESS.phone,
        "inLanguage": "it-IT",
        "petsAllowed": true,
        ...(apt.max_guests ? { "occupancy": { "@type": "QuantitativeValue", "maxValue": apt.max_guests } } : {}),
        ...(apt.bedrooms ? { "numberOfBedrooms": apt.bedrooms } : {}),
        ...(apt.bathrooms ? { "numberOfBathroomsTotal": apt.bathrooms } : {}),
        "containsPlace": {
          "@type": "Accommodation",
          "additionalType": isHotel ? "https://schema.org/HotelRoom" : "https://schema.org/Apartment",
          "name": apt.title,
          ...(apt.bedrooms ? { "numberOfBedrooms": apt.bedrooms } : {}),
          ...(apt.bathrooms ? { "numberOfBathroomsTotal": apt.bathrooms } : {}),
          ...(apt.max_guests ? { "occupancy": { "@type": "QuantitativeValue", "maxValue": apt.max_guests } } : {}),
          "amenityFeature": (apt.amenities || []).map((a) => ({ "@type": "LocationFeatureSpecification", "name": a, "value": true })),
        },
        "amenityFeature": (apt.amenities || []).map((a) => ({ "@type": "LocationFeatureSpecification", "name": a, "value": true })),
        "address": { "@type": "PostalAddress", "streetAddress": apt.address || "", "addressLocality": "Dimaro Folgarida", "addressRegion": "Trentino-Alto Adige", "postalCode": "38025", "addressCountry": "IT" },
        ...(apt.latitude && apt.longitude ? { "geo": { "@type": "GeoCoordinates", "latitude": apt.latitude, "longitude": apt.longitude } } : {}),
        ...(offerPrice ? { "makesOffer": {
          "@type": "Offer",
          "identifier": apt.id,
          "priceCurrency": "EUR",
          "price": offerPrice,
          "availability": "https://schema.org/InStock",
          "url": canonicalUrl,
          "checkinTime": checkinTime,
          "checkoutTime": checkoutTime,
          "priceSpecification": { "@type": "PriceSpecification", "price": offerPrice, "priceCurrency": "EUR" },
        } } : {}),
      },
      {
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Home", "item": SITE_URL + "/" },
          { "@type": "ListItem", "position": 2, "name": apt.title, "item": canonicalUrl },
        ],
      },
      {
        "@type": "FAQPage",
        "mainEntity": aptFaq.map((f) => ({ "@type": "Question", "name": f.q, "acceptedAnswer": { "@type": "Answer", "text": f.a } })),
      },
    ],
  };

  return (
    <div className="bg-ivory min-h-screen">
      <Seo
        title={seoTitle}
        description={seoDesc}
        path={aptUrl(apt)}
        image={resolveImg(apt.images?.[0])}
        jsonLd={jsonLd}
      />
      <nav className="flex items-center justify-between px-6 md:px-12 py-5 bg-fir text-ivory">
        <Link to="/" className="flex items-center gap-2"><Mountain className="text-gold" size={24} /><span className="font-display text-xl">Appartamenti in Val di Sole</span></Link>
        <div className="flex items-center gap-5">
          <LanguageSwitcher />
          <Link to="/" data-testid="back-home" className="flex items-center gap-1 text-sm hover:text-gold"><ArrowLeft size={16} /> {t("Torna alla home")}</Link>
        </div>
      </nav>

      <div className="max-w-6xl mx-auto px-6 py-10">
        <div className="grid md:grid-cols-2 gap-4 mb-10">
          <img src={resolveImg(gallery[active])} alt={apt.title} className="rounded-3xl w-full h-[440px] object-cover" />
          <div className="grid grid-cols-2 gap-4">
            {gallery.map((g, i) => (
              <img key={i} src={resolveImg(g)} alt="" loading="lazy" onClick={() => setActive(i)}
                className={`rounded-2xl h-[210px] w-full object-cover cursor-pointer transition-opacity ${active === i ? "ring-2 ring-gold" : "opacity-80 hover:opacity-100"}`} />
            ))}
          </div>
        </div>

        {apt.panoramas?.length > 0 && (
          <div className="mb-12">
            <Panorama360 images={apt.panoramas.map((p) => resolveImg(p))} />
          </div>
        )}

        <div className="grid md:grid-cols-3 gap-10">
          <div className="md:col-span-2">
            <div className="inline-flex items-center gap-1.5 bg-fir/90 text-ivory px-3 py-1 rounded-full text-xs uppercase tracking-wide mb-3">
              {isHotel ? <><Mountain size={12} className="text-gold" /> Hotel</> : t("Appartamento")}
            </div>
            <h1 className="font-display text-4xl md:text-5xl text-fir">{t(apt.title)}</h1>
            <p className="text-wood mt-2 text-lg">{t(apt.subtitle)}</p>
            <p className="text-fir-deep/75 leading-relaxed mt-6 text-lg">{t(apt.description)}</p>
            <p className="text-fir-deep/70 leading-relaxed mt-4">
              {`${apt.title} è ${isHotel ? "una struttura ricettiva" : "un appartamento indipendente"} a Dimaro Folgarida, nel cuore della Val di Sole (Trentino), ai piedi delle Dolomiti di Brenta. `}
              {sizeLabel ? `Dispone di ${sizeLabel}. ` : ""}
              {topAmenities ? `Tra i servizi principali: ${topAmenities}. ` : ""}
              {"È una base comoda per raggiungere gli impianti di Folgarida-Marilleva d'inverno e per rafting sul Noce, e-bike e trekking nelle Dolomiti di Brenta d'estate."}
            </p>
            {apt.cin && (
              <p data-testid="apt-cin" className="mt-3 text-sm text-fir-deep/60">
                <span className="uppercase tracking-wide text-fir-deep/45">CIN</span>{" "}
                <span className="font-medium text-fir-deep/80">{apt.cin}</span>
              </p>
            )}

            <h3 className="font-display text-2xl text-fir mt-10 mb-4">{t("Servizi")}</h3>
            <div className="grid grid-cols-2 gap-3">
              {(apt.amenities || []).map((a) => {
                const Icon = ICONS[a] || Sparkles;
                return <div key={a} className="flex items-center gap-2 text-fir-deep/80"><Icon size={18} className="text-wood" />{t(a)}</div>;
              })}
            </div>

            {(apt.house_rules || []).length > 0 && (
              <>
                <h3 className="font-display text-2xl text-fir mt-10 mb-4">{t("Regole della casa")}</h3>
                <ul className="space-y-2">
                  {(apt.house_rules || []).map((r) => (
                    <li key={r} className="flex items-center gap-2 text-fir-deep/80"><Check size={16} className="text-gold" />{t(r)}</li>
                  ))}
                </ul>
              </>
            )}
          </div>

          <div>
            {isHotel ? (
              <div className="sticky top-6 bg-white rounded-3xl border border-wood/30 p-6 shadow-lg">
                <p className="font-display text-2xl text-fir mb-1">{t("Tipologie di camera")}</p>
                <p className="text-xs text-fir-deep/50 mb-4">{t("Prezzo per persona a notte · scegli e prenota.")}</p>
                <div className="space-y-3" data-testid="room-types-list">
                  {(apt.room_types || []).map((rt) => (
                    <div key={rt.id} data-testid="room-type-card" className="rounded-2xl border border-stone overflow-hidden bg-ivory">
                      {rt.images?.[0] && <img src={resolveImg(rt.images[0])} alt={rt.name} loading="lazy" className="w-full h-28 object-cover" />}
                      <div className="p-4">
                        <div className="flex items-center justify-between">
                          <h4 className="font-display text-lg text-fir">{t(rt.name)}</h4>
                          <span className="text-fir font-semibold">{t("da")} €{rt.base_price}</span>
                        </div>
                        <p className="text-xs text-fir-deep/60 flex items-center gap-1 mt-1"><Users size={12} /> {t("fino a")} {rt.max_guests} {t("ospiti")}</p>
                        <button data-testid="room-book-btn" onClick={() => bookRoom(rt)}
                          className="gold-btn w-full py-2.5 rounded-xl mt-3 text-sm">{t("Prenota")} {t(rt.name)}</button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="sticky top-6 bg-white rounded-3xl border border-wood/30 p-7 shadow-lg">
                <p className="text-fir-deep/60 text-sm">{t("A partire da")}</p>
                <p className="font-display text-4xl text-fir mb-1">€{apt.from_price ?? apt.base_price}<span className="text-lg text-fir-deep/50">/{t("persona/notte")}</span></p>
                <p className="text-xs text-fir-deep/50 mb-5">{t("Prezzo dinamico secondo stagione ed eventi")}</p>
                <button data-testid="detail-book-btn" onClick={() => bookRoom(null)} className="gold-btn w-full py-3.5 rounded-xl text-lg">{t("Prenota ora")}</button>
              </div>
            )}
          </div>
        </div>

        {/* AVAILABILITY CALENDAR */}
        <div className="mt-14">
          <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
            <h3 className="font-display text-3xl text-fir">{t("Disponibilità")}</h3>
            {isHotel && (apt.room_types || []).length > 0 && (
              <div className="flex flex-wrap gap-2" data-testid="cal-room-tabs">
                {apt.room_types.map((rt) => (
                  <button key={rt.id} type="button" data-testid={`cal-room-tab-${rt.id}`}
                    onClick={() => setCalRoomId(rt.id)}
                    className={`px-4 py-1.5 rounded-full text-sm transition-colors ${activeCalRoom === rt.id ? "bg-fir text-ivory" : "bg-white border border-stone text-fir-deep/70 hover:border-fir"}`}>
                    {t(rt.name)}
                  </button>
                ))}
              </div>
            )}
          </div>
          <AvailabilityCalendar
            apartmentId={apt.id}
            roomTypeId={activeCalRoom}
            onPick={(isoDate) => pickDate(isoDate, isHotel ? (apt.room_types || []).find((r) => r.id === activeCalRoom) : null)}
          />
        </div>

        {/* IDEALE PER + POSIZIONE */}
        <div className="mt-16 grid md:grid-cols-2 gap-10">
          <div>
            <h2 className="font-display text-2xl text-fir mb-3">{t("Ideale per")}</h2>
            <p className="text-fir-deep/75 leading-relaxed">
              {t("Coppie e famiglie in cerca di una base comoda in Val di Sole: settimane bianche sugli impianti di Folgarida-Marilleva, vacanze estive tra rafting sul Noce, e-bike e trekking, e weekend di relax ai piedi delle Dolomiti di Brenta.")}
            </p>
          </div>
          <div>
            <h2 className="font-display text-2xl text-fir mb-3">{t("Posizione e vantaggi")}</h2>
            <ul className="space-y-2">
              {[
                "A Dimaro, nel cuore della Val di Sole (Trentino)",
                "A pochi minuti dagli impianti di Folgarida-Marilleva",
                "Vicino a Madonna di Campiglio e alle Dolomiti di Brenta",
                "Prenotazione diretta al miglior prezzo, senza commissioni",
              ].map((x) => (
                <li key={x} className="flex items-start gap-2 text-fir-deep/80"><Check size={16} className="text-gold mt-1 shrink-0" />{t(x)}</li>
              ))}
            </ul>
          </div>
        </div>

        {/* FAQ */}
        <div className="mt-16" data-testid="apt-faq">
          <h2 className="font-display text-3xl text-fir mb-6">{t("Domande frequenti")}</h2>
          <div className="space-y-3">
            {aptFaq.map((f, i) => (
              <details key={i} data-testid="apt-faq-item" className="bg-white rounded-2xl border border-stone p-5 group">
                <summary className="font-medium text-fir cursor-pointer list-none flex items-center justify-between">{f.q}<ArrowLeft size={18} className="text-gold rotate-180 group-open:rotate-[270deg] transition-transform" /></summary>
                <p className="text-fir-deep/75 mt-3 leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </div>

        {/* POSITION & MAP */}
        <div className="mt-14" data-testid="property-map-section">
          <h3 className="font-display text-3xl text-fir mb-2">{t("Dove si trova")}</h3>
          {apt.address && (
            <p className="flex items-center gap-2 text-fir-deep/75 mb-4"><MapPin size={18} className="text-gold" /> {apt.address}</p>
          )}
          <iframe
            title={`Mappa ${apt.title}`}
            className="w-full h-[360px] rounded-3xl border border-stone"
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            src={`https://www.google.com/maps?q=${encodeURIComponent(mapQuery)}&z=15&output=embed`}
          />
          {apt.latitude && apt.longitude && (
            <a href={`https://www.google.com/maps/search/?api=1&query=${apt.latitude},${apt.longitude}`} target="_blank" rel="noreferrer"
              className="inline-flex items-center gap-1.5 mt-3 text-sm text-fir hover:text-gold">
              <MapPin size={15} /> {t("Apri in Google Maps")}
            </a>
          )}
        </div>
      </div>

      <footer className="mt-16 border-t border-stone py-6 px-6 md:px-16">
        <div className="max-w-6xl mx-auto flex flex-wrap items-center justify-between gap-3 text-sm text-fir-deep/60">
          <span>© {new Date().getFullYear()} Appartamenti in Val di Sole · Val di Sole (TN)</span>
          <div className="flex gap-4">
            <Link to="/privacy" className="hover:text-gold">Privacy</Link>
            <Link to="/cookie-policy" className="hover:text-gold">Cookie Policy</Link>
          </div>
        </div>
      </footer>

      <BookingModal open={open} onClose={() => setOpen(false)} apartment={apt} roomType={selectedRoom}
        initial={bookingInitial} />
      <ConciergeWidget />
    </div>
  );
}

